import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  Home,
  MapPin,
  School,
  ShieldCheck,
} from "lucide-react";
import { VARIANT, VIP_LOGIN_URL } from "@/constants/links";
import { track } from "@/lib/tracker";

const BULLETS_BASE = [
  "Report giornaliero del tempo schermo",
  "Modalità Studio attivabile all'istante",
  "Stato della batteria e posizione condivisa",
  "Filtro per categorie di app",
];

const BULLETS =
  VARIANT === "vip"
    ? [
        "Attività social e di messaggistica (WhatsApp, TikTok e altre app supportate)",
        ...BULLETS_BASE,
        "Media remoti: screenshot, fotocamera e microfono su autorizzazione",
      ]
    : BULLETS_BASE;

const USAGE = [
  { app: "Roblox", time: "45m", w: "64%", color: "#7763E5" },
  { app: "YouTube", time: "38m", w: "54%", color: "#FFD335" },
  { app: "Musica", time: "22m", w: "31%", color: "#E8558F" },
  { app: "Altro", time: "30m", w: "42%", color: "#60A5FA" },
];

const RuleRow = ({ icon: Icon, label, on, onToggle, testId }) => (
  <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm">
    <div className="flex items-center gap-3">
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-xl ${
          on ? "bg-softpurple text-violet" : "bg-slate-100 text-slate-400"
        }`}
      >
        <Icon size={15} />
      </span>
      <span className="text-[13px] font-semibold text-ink">{label}</span>
    </div>
    <button
      data-testid={testId}
      aria-pressed={on}
      aria-label={label}
      onClick={onToggle}
      className={`relative h-6 w-11 rounded-full transition-colors duration-200 ${
        on ? "bg-violet" : "bg-slate-300"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ${
          on ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  </div>
);

export const ParentPanel = () => {
  const [tab, setTab] = useState("oggi");
  const [rules, setRules] = useState({ studio: true, social: true, notte: true, acquisti: false });
  const toggle = (key) => setRules((r) => ({ ...r, [key]: !r[key] }));

  return (
    <section
      id="pannello"
      className="relative scroll-mt-24 overflow-hidden bg-ink py-24 text-white md:py-32"
    >
      <div aria-hidden="true" className="absolute inset-0 bg-dots-light opacity-60" />
      <div
        aria-hidden="true"
        className="absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(119,99,229,0.35), transparent)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -right-32 bottom-0 h-[400px] w-[400px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(255,211,53,0.14), transparent)" }}
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-yellow/15 px-4 py-2 text-xs font-bold uppercase tracking-widest text-yellow">
            Pannello Genitore
          </span>
          <h2 className="mt-5 font-display text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">
            Tutto <span className="text-yellow underline decoration-yellow/40 decoration-8 underline-offset-4">sotto controllo</span>, con un colpo d'occhio.
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-white/70 sm:text-lg">
            Statistiche chiare e regole immediate, senza complicazioni tecniche. Dal pannello
            gestisci la famiglia tutta in una volta.
          </p>

          <ul className="mt-9 space-y-4">
            {BULLETS.map((b, i) => (
              <motion.li
                key={b}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-center gap-3 text-[15px] font-medium text-white/90"
              >
                <CheckCircle2 size={19} className="shrink-0 text-yellow" />
                {b}
              </motion.li>
            ))}
          </ul>

          {VARIANT === "vip" ? (
            <a
              data-testid="panel-login-link"
              href={VIP_LOGIN_URL}
              onClick={() => track("login_click")}
              className="btn-3d-yellow mt-10 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-extrabold"
            >
              Accedi alla dashboard VIP
              <ArrowUpRight size={16} />
            </a>
          ) : (
            <p className="mt-10 inline-flex items-center gap-2 rounded-full border-2 border-cream/20 px-6 py-3 text-sm font-semibold text-cream/80">
              Incluso nell'app Guardian Family per Android
            </p>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto w-[300px] sm:w-[330px]"
        >
          <div className="overflow-hidden rounded-[3rem] border-[10px] border-black/70 bg-black/70 shadow-2xl shadow-black/60">
            <div className="relative min-h-[560px] bg-[#FBFAF7] p-5 pt-9 text-ink">
              <div className="absolute left-1/2 top-2 z-10 h-5 w-28 -translate-x-1/2 rounded-b-2xl bg-black/70" />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-bodytext">
                    Pannello Genitore
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow text-xs font-extrabold text-ink">
                      S
                    </span>
                    <span className="text-sm font-bold">Sofia</span>
                    <span className="flex items-center gap-1 rounded-full bg-softgreen px-2 py-0.5 text-[10px] font-bold text-emerald-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-mint" />
                      online
                    </span>
                  </div>
                </div>
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-softpurple text-violet">
                  <ShieldCheck size={17} />
                </span>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-1 rounded-full bg-softpurple p-1">
                {[
                  { id: "oggi", label: "Oggi" },
                  { id: "regole", label: "Regole" },
                  { id: "posizione", label: "Posizione" },
                ].map((t) => (
                  <button
                    key={t.id}
                    data-testid={`panel-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`rounded-full py-1.5 text-xs font-bold transition-all ${
                      tab === t.id
                        ? "bg-violet text-white shadow"
                        : "text-ink/50 hover:text-ink"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="mt-4 min-h-[360px]">
                {tab === "oggi" && (
                  <div data-testid="panel-view-oggi">
                    <div className="rounded-3xl bg-white p-4 shadow-sm">
                      <p className="text-[11px] font-semibold text-bodytext">Tempo schermo oggi</p>
                      <p className="mt-1 font-display text-3xl font-black text-ink">2h 15m</p>
                      <p className="mt-1 text-[11px] font-bold text-violet">−20% rispetto a ieri</p>
                    </div>
                    <div className="mt-3 space-y-3 rounded-3xl bg-white p-4 shadow-sm">
                      {USAGE.map((u) => (
                        <div key={u.app}>
                          <div className="flex items-center justify-between text-[12px] font-semibold text-ink/70">
                            <span>{u.app}</span>
                            <span>{u.time}</span>
                          </div>
                          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-softpurple/60">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: u.w, backgroundColor: u.color }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "regole" && (
                  <div className="space-y-2.5" data-testid="panel-view-regole">
                    <RuleRow
                      icon={BookOpen}
                      label="Modalità Studio"
                      on={rules.studio}
                      onToggle={() => toggle("studio")}
                      testId="panel-toggle-studio"
                    />
                    <RuleRow
                      icon={ShieldCheck}
                      label="Filtro contenuti social"
                      on={rules.social}
                      onToggle={() => toggle("social")}
                      testId="panel-toggle-social"
                    />
                    <RuleRow
                      icon={MapPin}
                      label="Fascia notturna 22:00 – 7:00"
                      on={rules.notte}
                      onToggle={() => toggle("notte")}
                      testId="panel-toggle-notte"
                    />
                    <RuleRow
                      icon={ShieldCheck}
                      label="Acquisti in-app"
                      on={rules.acquisti}
                      onToggle={() => toggle("acquisti")}
                      testId="panel-toggle-acquisti"
                    />
                  </div>
                )}

                {tab === "posizione" && (
                  <div data-testid="panel-view-posizione">
                    <div
                      className="relative h-52 overflow-hidden rounded-3xl bg-[#EAF3EA] shadow-sm"
                      style={{
                        backgroundImage:
                          "linear-gradient(rgba(31,31,37,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(31,31,37,0.05) 1px, transparent 1px)",
                        backgroundSize: "28px 28px",
                      }}
                    >
                      <span className="absolute left-[22%] top-[30%] flex h-9 w-9 items-center justify-center rounded-full bg-white text-violet shadow-md">
                        <Home size={15} />
                      </span>
                      <span className="absolute right-[18%] top-[22%] flex h-9 w-9 items-center justify-center rounded-full bg-white text-pinky-deep shadow-md">
                        <School size={15} />
                      </span>
                      <span className="absolute left-[48%] top-[58%] flex items-center gap-1.5 rounded-full bg-yellow px-3 py-1.5 text-[10px] font-extrabold text-ink shadow-lg">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink" />
                        Sofia è qui
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between rounded-3xl bg-white p-4 shadow-sm">
                      <div>
                        <p className="text-[11px] font-semibold text-bodytext">Luoghi sicuri attivi</p>
                        <p className="mt-0.5 text-sm font-bold text-ink">Casa · Scuola</p>
                      </div>
                      <span className="rounded-full bg-softgreen px-2.5 py-1 text-[10px] font-bold text-emerald-600">
                        Attivo
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="mt-5 text-center text-xs text-white/50">
            Anteprima illustrativa dell'app · dati di esempio
          </p>
        </motion.div>
      </div>
    </section>
  );
};
